import { BluDeltaVersion, bludeltaRequest } from './network';
import { OCRResult } from './ocrResult';
import {
  InvoicePredictionDetailType,
  InvoiceDetailType,
  InvoiceDetailContactType,
  InvoiceDetailContactAddressType,
} from './invoiceDetails';

/**
 * Special Invoice format.
 * *for future use only*
 */
export enum DetectInvoiceFormat {
  None = 0,
  EbInterface = 1,
}

export interface DetectInvoiceParams {
  /** Version of the BLU DELTA Webservice. */
  Version: BluDeltaVersion;
  /**
   * Invoice (encoded as base64 string)
   * accepted formats: *PDF, JPG, PNG, GIF, TIFF*
   */
  Invoice: Base64;
  /**
   * Filter for Invoice details that shall be predicted
   * flag 'None' will return all available Invoice details for your subscription,
   * else define the filter by providing a bitmask for the Invoice details that shall be predicted.
   * e.g. Filter = GrandTotalAmount | Ibans.
   */
  Filter?: 0; // TODO-db-191014 implement filter if necessary
  Format?: DetectInvoiceFormat;
  /** Additional properties that will be used for invoice processing, provided as Key-Value pairs. */
  PropertyStore?: Record<string, string>;
  /** If this flag is set, the response will contain the sent invoice as pdf-document with marked predictions. */
  CreateResultPdf?: boolean;
  /** If this flag is set, the response will contain the OCR result as json string. */
  AddOcrResult?: boolean;
}

export enum InvoiceState {
  OK = 0,
  PredictionFailed = 1,
  PDFResultFailed = 2,
  OCRAndPDFResultFailed = 3,
  OCRResultFailed = 4,
}

export interface Detection {
  /** Predicted invoice detail. */
  Type: unknown;
  /** Predicted invoice detail. */
  TypeName: InvoicePredictionDetailType | string;
  /**
   * `GrandTotalAmount`: decimal separator '.', no group separator, two decimal digits, e.g. 9.00, 2314.50, -12.99
   * `NetAmount`: decimal separator '.', no group separator, two decimal digits, e.g. 9.00, 2314.50, -12.99
   * `VatAmount`: decimal separator '.', no group separator, two decimal digits, e.g. 9.00, 2314.50, -12.99
   * `VatRate`: decimal separator '.', no group separator, one decimal digits, e.g. 20.0, 19.0, 10.0
   * `InvoiceDate`: YYYY-MM-DD (ISO 8601)
   * `DeliveryDate`: YYYY-MM-DD (ISO 8601)
   * `SenderOrderDate`: YYYY-MM-DD (ISO 8601)
   * `ReceiverOrderDate`: YYYY-MM-DD (ISO 8601)
   * `InvoiceId`: string
   * `InvoiceCurrency`: string
   * `CustomerId`: string
   * `SenderOrderId`: string
   * `ReceiverOrderId`: string
   * `DocumentType`: 'Invoice' or 'CreditMemo'
   * `Ibans`: string without blanks, comma separated
   * `UstIds`: string without blanks, comma separated
   * `DeliveryNoteId`: string
   * `Sender`: string
   */
  Value: string;
  /** Scored probability. Value between zero and one. */
  Score: number;
  /** X-Position of prediction according to the document resolution. */
  X: number;
  /** Y-Position of prediction according to the document resolution. */
  Y: number;
  /** Width of prediction according to the document resolution. */
  Width: number;
  /** Height of prediction according to the document resolution. */
  Height: number;
}

export interface DetectionGroup {
  /** Predicted invoice detail. */
  Type: unknown;
  /** Predicted invoice detail. */
  TypeName: InvoicePredictionDetailType;
  /** One for each predicted invoice detail. */
  InvoiceDetailTypePredictions: Detection[];
}

export interface DetectedDetail {
  /** Name of predicted detail. */
  Name: InvoiceDetailContactType | InvoiceDetailContactAddressType;
  /** Predicted value. */
  Value: string;
  /** Scored probability. Value between zero and one. */
  Score: number;
  /** X-Position of prediction according to the document resolution. */
  X: number;
  /** Y-Position of prediction according to the document resolution. */
  Y: number;
  /** Width of prediction according to the document resolution. */
  Width: number;
  /** Height of prediction according to the document resolution. */
  Height: number;
}

export interface DetectedAddress {
  /** Street (incl. street number). */
  Street: DetectedDetail;
  /** Zip code / postal code. */
  ZipCode: DetectedDetail;
  /** City. */
  City: DetectedDetail;
  /** Country. */
  Country: DetectedDetail;
  /** X-Position according to the document resolution. */
  X: number;
  /** Y-Position according to the document resolution. */
  Y: number;
  /** Width according to the document resolution. */
  Width: number;
  /** Height according to the document resolution. */
  Height: number;
}

export interface DetectedContact {
  /** Name of the contact. */
  Name: DetectedDetail;
  /** Address of the contact. */
  Address: DetectedAddress;
  /** Scored probability. Value between zero and one. */
  Score: number;
  /** Website of the contact. */
  WebsiteUrl: DetectedDetail[];
  /** Email address of the contact. */
  Email: DetectedDetail[];
  /** Phone number of the contact. */
  Phone: DetectedDetail;
  /** Fax number of the contact. */
  Fax: DetectedDetail;
}

export interface DetectedSupplier {
  SupplierId: string;
  Name: string;
  Uids: string[];
  Ibans: string[];
  City: string;
  Country: string;
  Street: string;
  ZipCode: string;
}

export interface DetectedClient {
  Name: string;
  Id: string;
}

export interface DetectInvoiceResponse {
  /** Resolution of the original document in dpi. */
  DocumentResolution: number;
  /** Array of predictions. One for each predicted invoice detail. */
  InvoiceDetailTypePredictions: Detection[];
  /**
   * Array of prediction groups. One for each predicted invoice detail.
   * E.g. `VatGroup`, containing predictions for `VatRate`, `NetAmount` and `VatAmount`.
   */
  PredictionGroups: DetectionGroup[];
  LineItemTable: any;
  /** The sender (biller) of the invoice. */
  [InvoiceDetailType.Sender]?: DetectedContact;
  /** The receive of the invoice. */
  [InvoiceDetailType.Receiver]?: DetectedContact;
  /* Additional Properties */
  [InvoiceDetailType.AdditionalProperties]?: Record<string, string>;
  /*The invoice Supplier */
  [InvoiceDetailType.Supplier]?: DetectedSupplier;
  /*The invoice Client */
  [InvoiceDetailType.Client]?: DetectedClient;
  /** Special invoice format. (for future use only) */
  FormattedResult?: string;
  /** Invoice quality flag. (for future use only) */
  IsQualityOk: boolean;
  /**
   * Result pdf-document with marked predictions (encoded as base64 string)
   * *Will contain an empty string if the pdf could not be created successfully.*
   */
  ResultPdf?: Base64;
  /**
   * OCR Result (as json string).
   * *Will contain an empty string if the OCR result could not be created successfully.*
   */
  OcrResult?: OCRResult;
  /**
   * Specifies if the response contains all requested data.
   * The value is built from a bitmask for all tasks which could not successfully deliver a result.
   */
  InvoiceState: InvoiceState;
}

export function detectInvoice(params: DetectInvoiceParams): Promise<DetectInvoiceResponse> {
  // NOTE-db-190408 `any` is used here because the response does not contain `State`, rather: `InvoiceState`
  return bludeltaRequest<any>('POST', '/invoicedetail/detect', params);
}

export function createEmptyDetection(type : any, name : string)
{
  return <Detection> {
    Score: 0.00,
    Type: type,
    TypeName: (<any>InvoicePredictionDetailType)[name],
    Value: '',
    Height: 0,
    Width: 0,
    X: 0,
    Y: 0
  }
}

export function createEmptyDetectedDetail(name: string)
{
  return <DetectedDetail> {
    Height: 0,
    Name: name,
    Score: 0.00,
    Value: '',
    Width: 0,
    X: 0,
    Y: 0
  }
}

export function createEmptyDetectedContact()
{
  return <DetectedContact> {
    Address: <DetectedAddress> {
      City: createEmptyDetectedDetail('City'),
      Country: createEmptyDetectedDetail('Country'),
      Height: 0,
      Street: createEmptyDetectedDetail('Street'),
      Width: 0,
      X: 0,
      Y: 0,
      ZipCode: createEmptyDetectedDetail('ZipCode')
    },
    Email: [],
    Fax: createEmptyDetectedDetail('Fax'),
    Name: createEmptyDetectedDetail('Name'),
    Phone: createEmptyDetectedDetail('Phone'),
    Score: 0.0,
    WebsiteUrl: []
  }
}

export function createEmptyVatGroup()
{
  return <DetectionGroup> {
    Type: 536870912,
    TypeName: 'VatGroup',
    InvoiceDetailTypePredictions: [createEmptyDetection(32, 'VatRate'), createEmptyDetection(268435456, 'NetAmount'), createEmptyDetection(131072, 'VatAmount')]
  }
}
/*
export function createEmptyLineItemGroup()
{
  return <DetectionGroup> {
    Type: 536870912,
    TypeName: 'LineItemGroup',
    InvoiceDetailTypePredictions: [createEmptyDetection(268435456, 'TotalAmount')]
  }
}
*/
export function createEmptyDiscountGroup()
{
  return <DetectionGroup> {
    Type: 1099511627776,
    TypeName: 'DiscountGroup',
    InvoiceDetailTypePredictions: [createEmptyDetection(68719476736, 'DiscountDate'), createEmptyDetection(274877906944, 'DiscountDuration'), createEmptyDetection(549755813888, 'DiscountPercent')]
  }
}
export function createEmptyDueDateGroup()
{
  return <DetectionGroup> {
    Type: 17592186044416,
    TypeName: 'DueDateGroup',
    InvoiceDetailTypePredictions: [createEmptyDetection(2199023255552, 'DueDateDate'), createEmptyDetection(8796093022208, 'DueDateDuration')]
  }
}

export function createEmptyBankGroup()
{
  return <DetectionGroup> {
    Type: 17179869184,
    TypeName: 'BankGroup',
    InvoiceDetailTypePredictions: [createEmptyDetection(4294967296, 'BankCode'), createEmptyDetection(8589934592, 'BankAccount')]
  }
}
